/**
 * Read adapters for `price-curves` (plots plan G; docs/deck-design-system/plots/read-adapters.md).
 *
 * Shape only: rename, regroup, copy. No crossing, rounding or window is
 * computed here; every figure is the one `build_price_curve` published.
 *
 * - `summary-card: price_curve` (and the analysis output's `priceCurve`, which
 *   the survey tab's "Price range" card reads): the canonical `PriceCurve`
 *   field for field. Stored cards carry no chrome, so `labels` is empty and the
 *   surface supplies its own translated labels. **Legacy formatting:** a stored
 *   curve has no stamped `decimals`, so today's corridor rule (`priceDecimals`)
 *   stays here for it. A curve stored before `reported` / `displayWindow`
 *   existed is not accepted and keeps `PriceCurveChart` as its legacy reader.
 * - `deck-slide: price-sensitivity`: `band` to the two marginal points,
 *   `optimumPrice` to `optimalPricePoint` (null = withheld), `displayLabels` to
 *   the `price_sensitivity_*` label keys; no `indifferencePricePoint`.
 * - `quick-study: psm`: a result carrying the Python-built `price_curve_plot`
 *   passes through. A stored result with only the legacy `psm` (`PSMResult`,
 *   a second algorithm) is not accepted and keeps `PSMChart` as its legacy
 *   reader.
 */

import { PriceCurve, PriceCurvePayload } from '@digital-twins/shared-types'

import { priceDecimals } from '../../foundations/price-decimals'
import { PriceSensitivitySlideData } from '../../slides/renderer-internal-types'
import { PlotReadAdapter, StoredPlotPayload } from '../contract'
import { PriceCurvesPlotLabels } from './PriceCurvesPlot'

type StoredPriceCurves = StoredPlotPayload<'price-curves'>
type StoredData = StoredPriceCurves['data']

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function hasRange(value: unknown): boolean {
  return isRecord(value) && typeof value.low === 'number' && typeof value.high === 'number'
}

/** True when a canonical curve carries what the registry contract requires. */
export function isPlottablePriceCurve(curve: unknown): curve is PriceCurve {
  return (
    isRecord(curve) &&
    Array.isArray(curve.series) &&
    curve.series.length > 0 &&
    typeof curve.baseSize === 'number' &&
    typeof curve.pointOfMarginalCheapness === 'number' &&
    typeof curve.pointOfMarginalExpensiveness === 'number' &&
    hasRange(curve.reported) &&
    hasRange(curve.displayWindow)
  )
}

/** The canonical `PriceCurve` as the kind's stored payload, with no chrome. */
export function readCanonicalPriceCurve(curve: PriceCurve): StoredPriceCurves {
  const reported = curve.reported as NonNullable<PriceCurve['reported']>
  const window = curve.displayWindow as NonNullable<PriceCurve['displayWindow']>
  const point = reported.point ?? null
  const data: StoredData = {
    series: curve.series.map((entry) => ({
      role: entry.role,
      label: entry.label,
      points: entry.points.map((p) => ({ price: p.price, share: p.share })),
    })),
    baseSize: curve.baseSize,
    currencyCode: curve.currencyCode ?? null,
    // Legacy formatting: no stamped decimals on a stored curve.
    decimals: priceDecimals([reported.low, reported.high, point]),
    pointOfMarginalCheapness: curve.pointOfMarginalCheapness,
    optimalPricePoint: point == null ? null : curve.optimalPricePoint,
    indifferencePricePoint: curve.indifferencePricePoint,
    pointOfMarginalExpensiveness: curve.pointOfMarginalExpensiveness,
    reported: { low: reported.low, high: reported.high, point },
    displayWindow: { low: window.low, high: window.high },
    respondentCount: curve.respondentCount ?? null,
    completeCount: curve.completeCount ?? null,
    inconsistentCount: curve.inconsistentCount ?? null,
    isThinBase: curve.isThinBase ?? null,
    reference: curve.reference
      ? {
          value: curve.reference.value,
          baseSize: curve.reference.baseSize,
          questionId: curve.reference.questionId,
          displayDecimals: curve.reference.displayDecimals ?? null,
        }
      : null,
    labels: {},
  }
  return { kind: 'price-curves', data }
}

export const summaryCardPriceCurveAdapter: PlotReadAdapter<'price-curves', PriceCurvePayload> = {
  kind: 'price-curves',
  source: { surface: 'summary-card', shape: 'price_curve' },
  isLegacy: false,
  accepts(stored: unknown): stored is PriceCurvePayload {
    return isRecord(stored) && stored.shape === 'price_curve' && isPlottablePriceCurve(stored.curve)
  },
  read(stored: PriceCurvePayload): StoredPriceCurves {
    return readCanonicalPriceCurve(stored.curve)
  },
}

/** Stored slide `displayLabels` field -> the kind's label key. */
const SLIDE_LABEL_KEYS: ReadonlyArray<[keyof NonNullable<PriceSensitivitySlideData['displayLabels']>, keyof PriceCurvesPlotLabels]> = [
  ['rangeLabel', 'price_sensitivity_range_label'],
  ['rangeSeparator', 'price_sensitivity_range_separator'],
  ['optimumLabel', 'price_sensitivity_optimum_label'],
  ['pointWithheldLabel', 'price_sensitivity_point_withheld'],
  ['lowerEndpointLabel', 'price_sensitivity_lower_endpoint'],
  ['upperEndpointLabel', 'price_sensitivity_upper_endpoint'],
  ['referenceLabel', 'price_sensitivity_reference_label'],
  // Stored already filled ("(base 81)"): a template with no placeholder left.
  ['referenceBaseLabel', 'price_sensitivity_reference_base'],
  ['yAxisTitle', 'price_sensitivity_y_axis_title'],
]

export const priceSensitivitySlideAdapter: PlotReadAdapter<'price-curves', PriceSensitivitySlideData> = {
  kind: 'price-curves',
  source: { surface: 'deck-slide', shape: 'price-sensitivity' },
  isLegacy: false,
  accepts(stored: unknown): stored is PriceSensitivitySlideData {
    return (
      isRecord(stored) &&
      stored.type === 'price-sensitivity' &&
      Array.isArray(stored.series) &&
      hasRange(stored.band) &&
      hasRange(stored.reported) &&
      hasRange(stored.displayWindow) &&
      typeof stored.baseSize === 'number'
    )
  },
  read(stored: PriceSensitivitySlideData): StoredPriceCurves {
    const labels: PriceCurvesPlotLabels = {}
    const displayLabels = stored.displayLabels ?? {}
    for (const [field, key] of SLIDE_LABEL_KEYS) {
      const text = displayLabels[field]
      if (typeof text === 'string' && text) {
        labels[key] = text
      }
    }
    // The stored series labels are the deck-language role labels.
    for (const entry of stored.series) {
      if (entry.label) {
        labels[`price_role_${entry.role}` as const] = entry.label
      }
    }
    const point = stored.reported.point ?? null
    const data: StoredData = {
      series: stored.series.map((entry) => ({
        role: entry.role,
        label: entry.label,
        points: entry.points.map((p) => ({ price: p.price, share: p.share })),
      })),
      baseSize: stored.baseSize,
      currencyCode: stored.currencyCode ?? null,
      // Legacy formatting: the slide's own corridor rule, as it prints today.
      decimals: priceDecimals([stored.reported.low, stored.reported.high, point]),
      pointOfMarginalCheapness: stored.band.low,
      optimalPricePoint: point == null ? null : stored.optimumPrice ?? null,
      pointOfMarginalExpensiveness: stored.band.high,
      reported: { low: stored.reported.low, high: stored.reported.high, point },
      displayWindow: { low: stored.displayWindow.low, high: stored.displayWindow.high },
      respondentCount: stored.respondentCount ?? null,
      completeCount: stored.completeCount ?? null,
      inconsistentCount: stored.inconsistentCount ?? null,
      isThinBase: stored.isThinBase ?? null,
      reference: stored.reference
        ? {
            value: stored.reference.value,
            baseSize: stored.reference.baseSize,
            displayDecimals: stored.reference.displayDecimals ?? null,
          }
        : null,
      labels,
    }
    return { kind: 'price-curves', data }
  },
}

/** A stamped `price-curves` plot, by the fields present (never by what a value means). */
export function isStampedPriceCurvesPlot(plot: unknown): plot is StoredPriceCurves {
  return (
    isRecord(plot) &&
    plot.kind === 'price-curves' &&
    isRecord(plot.data) &&
    Array.isArray(plot.data.series) &&
    typeof plot.data.decimals === 'number' &&
    hasRange(plot.data.reported) &&
    hasRange(plot.data.displayWindow)
  )
}

/** What a price slide read returns: the payload and whether Python stamped it. */
export interface PriceSensitivitySlideRead {
  payload: StoredPriceCurves
  isStamped: boolean
}

/**
 * A deck price-sensitivity slide through its stamped `plot` (plots wave 7, D2),
 * else through `priceSensitivitySlideAdapter` for a slide stored before the
 * stamp.
 */
export function readPriceSensitivitySlide(slide: PriceSensitivitySlideData & { plot?: unknown }): PriceSensitivitySlideRead {
  if (isStampedPriceCurvesPlot(slide.plot)) {
    return { payload: slide.plot, isStamped: true }
  }
  return { payload: priceSensitivitySlideAdapter.read(slide), isStamped: false }
}

/** A quick-study result's `price_curve_plot` (Python-built, labels stamped). */
export interface QuickStudyPriceCurvesSource {
  price_curve_plot?: unknown
}

export const quickStudyPriceCurvesAdapter: PlotReadAdapter<'price-curves', QuickStudyPriceCurvesSource> = {
  kind: 'price-curves',
  source: { surface: 'quick-study', shape: 'psm' },
  isLegacy: false,
  accepts(stored: unknown): stored is QuickStudyPriceCurvesSource {
    if (!isRecord(stored) || !isRecord(stored.price_curve_plot)) {
      return false
    }
    const plot = stored.price_curve_plot
    return plot.kind === 'price-curves' && isRecord(plot.data) && hasRange(plot.data.reported) && hasRange(plot.data.displayWindow)
  },
  read(stored: QuickStudyPriceCurvesSource): StoredPriceCurves {
    const plot = stored.price_curve_plot as StoredPriceCurves
    return { kind: 'price-curves', data: { ...plot.data, labels: { ...(plot.data.labels ?? {}) } } }
  },
}

export const PRICE_CURVES_ADAPTERS = [
  summaryCardPriceCurveAdapter,
  priceSensitivitySlideAdapter,
  quickStudyPriceCurvesAdapter,
] as const
